export type Locale = "zh" | "en";

const EN = {
  otherLocale: "繁體中文",
  title: "Student access",
  subtitle:
    "Why a student can or cannot see a project. Pick no project to list everything they can see.",
  studentLabel: "Student",
  studentPlaceholder: "username, user id, or MyBestUser id",
  projectLabel: "Project (optional)",
  clearProject: "Clear project",
  chooseProject: "Choose a project",
  check: "Check",
  checking: "Checking...",
  howItWorks: "How visibility works",
  rules: [
    "Visibility has two layers: their ticket decides which products they may open, and their classroom decides how far up the levels they may go.",
    "Products come from the student's tickets. A product no ticket grants is invisible however their class is placed.",
    "The level cap is the classroom's course level for that product — the highest one when several classrooms teach it.",
    "A product no classroom course teaches has no level at all, so it stays invisible even when the ticket grants it.",
    "A project carrying two products takes the higher class level of the two, even when the ticket covers only one of them.",
    "A course MyBestUser marks invalid still sets the level. The school's contract is a billing matter and does not affect what a student sees.",
    "Staff are the exception: their products come from their MyBestUser product list, no class places them, and they reach every level of whatever that list allows.",
    "This page reads MyBestUser live. The student's own app reads at login, so a change still needs them to sign out and back in before they see it.",
    "A folder above the project must pass the same checks, or the student never reaches it by browsing.",
  ],
  school: "School",
  noSchool: "none",
  classrooms: "Classrooms",
  noClassrooms: "none — no course caps their level",
  ticketExpires: "Ticket expires",
  noTicket: "no ticket recorded",
  signedIn: (at: string) =>
    `Signed in ${at} — changes made in MyBestUser after this need a fresh sign-in`,
  capHeld: "On their ticket",
  capNotHeld: "Not on their ticket",
  noSession: "No session — this student is not signed in anywhere.",
  visible: "They can see it",
  notVisible: "They cannot see it",
  visibleProjects: (count: number) => `Can see ${count} top-level project${count === 1 ? "" : "s"}`,
  nothingVisible: "Nothing at all. Check their tickets and the course levels above.",
};

const ZH: typeof EN = {
  otherLocale: "English",
  title: "學生可見性",
  subtitle: "說明學生為何看得到或看不到某個專案。不選專案，則列出他們能看到的全部內容。",
  studentLabel: "學生",
  studentPlaceholder: "帳號、使用者 ID 或 MyBestUser ID",
  projectLabel: "專案（選填）",
  clearProject: "清除專案",
  chooseProject: "選擇專案",
  check: "檢查",
  checking: "檢查中…",
  howItWorks: "可見性的判斷方式",
  rules: [
    "可見性分兩層：票券決定他們可以開啟哪些產品，班級決定他們可以看到多高的等級。",
    "產品來自學生的票券；票券沒有授予的產品，無論班級如何安排都看不到。",
    "等級上限取該產品所屬班級的課程等級 — 若有多個班級教同一產品，取其中最高者。",
    "若沒有任何班級課程教該產品，該產品就沒有等級，即使票券有授予也看不到。",
    "一個專案帶有兩項產品時，取兩者班級等級較高的那個，即使票券只涵蓋其中一項。",
    "被 MyBestUser 標記為無效的課程仍然決定等級。學校合約屬於帳務問題，不影響學生看得到什麼。",
    "教職員為例外：其產品來自 MyBestUser 的產品清單，不受班級安排限制，清單內的產品可看到所有等級。",
    "本頁即時讀取 MyBestUser；學生的 App 則在登入時讀取，因此變更後仍需他們登出並重新登入才會看到。",
    "專案上層的資料夾必須通過同樣的檢查，否則學生無法透過瀏覽抵達。",
  ],
  school: "學校",
  noSchool: "無",
  classrooms: "班級",
  noClassrooms: "無 — 沒有課程限制其等級",
  ticketExpires: "票券到期",
  noTicket: "沒有票券紀錄",
  signedIn: (at: string) => `於 ${at} 登入 — 此後在 MyBestUser 的變更需重新登入才會生效`,
  capHeld: "票券內",
  capNotHeld: "不在票券內",
  noSession: "沒有工作階段 — 這位學生目前未在任何裝置登入。",
  visible: "看得到",
  notVisible: "看不到",
  visibleProjects: (count: number) => `看得到 ${count} 個頂層專案`,
  nothingVisible: "完全沒有。請檢查上方的票券與課程等級。",
};

export const COPY: Record<Locale, typeof EN> = { en: EN, zh: ZH };

const ROLES: Record<string, string> = {
  student: "學生",
  teacher: "老師",
  principal: "校長",
  secretary: "行政",
  admin: "管理員",
  superuser: "超級管理員",
};

/** A failing check carries its own label, so both wordings of each step appear here. */
const CHECK_LABELS: Record<string, string> = {
  "Signed in": "已登入",
  "Not signed in": "未登入",
  "Session matches MyBestUser": "工作階段與 MyBestUser 一致",
  "Session no longer matches MyBestUser": "工作階段與 MyBestUser 不一致",
  "Session could not be compared with MyBestUser": "無法與 MyBestUser 比對工作階段",
  "Project exists": "專案存在",
  "Project does not exist": "專案不存在",
  "Project is visible to students": "專案對學生可見",
  "Project is hidden from students": "專案對學生隱藏",
  "Product is on their ticket": "票券包含此產品",
  "Product is not on their ticket": "票券不包含此產品",
  "Product is in their classroom": "班級有教此產品",
  "Product is not in their classroom": "班級沒有教此產品",
  "Level is within their class level": "等級在班級等級之內",
  "Level is above their class level": "等級超過班級等級",
  "Reachable by browsing": "可透過瀏覽抵達",
  "Not reachable by browsing": "無法透過瀏覽抵達",
};

const ZERO_LEVEL_NOTE = "那是產品的基礎等級，通常沒有內容 — 請確認班級的課程等級。";

/** Chinese lists read with 、 rather than the comma the API joins with. */
function zhList(text: string): string {
  return text.split(", ").join("、");
}

/** `Cosmic at 3 (Level 1)` — the API's placement phrasing — becomes `Cosmic 到 3 (Level 1)`. */
function zhPlacements(text: string): string {
  return text
    .split(", ")
    .map((entry) => {
      const parts = entry.match(/^(.+?) at (.+)$/);
      return parts ? `${parts[1]} 到 ${parts[2]}` : entry;
    })
    .join("、");
}

/**
 * The API writes its check details as English prose, so Chinese is recovered by matching that
 * wording (see `studentAccess.service.ts`). An unmatched detail falls back to the English.
 */
const DETAILS: [RegExp, (match: RegExpMatchArray) => string][] = [
  [/^They are signed in on at least one device\.$/, () => "他們至少在一台裝置上已登入。"],
  [/^Nothing is waiting on a fresh sign-in\.$/, () => "沒有任何變更在等待重新登入。"],
  [/^Have them sign out and back in\.$/, () => "請學生登出後重新登入。"],
  [
    /^MyBestUser could not be read, so this is the session's own snapshot\.$/,
    () => "無法讀取 MyBestUser，因此這裡顯示的是工作階段的快照。",
  ],
  [/^Level (\d+), id (\w+)\.$/, (m) => `等級 ${m[1]}，ID ${m[2]}。`],
  [/^No project with that id\.$/, () => "找不到這個 ID 的專案。"],
  [
    /^"(.+)" is (deleted|in the trash)\.$/,
    (m) => `「${m[1]}」${m[2] === "deleted" ? "已刪除" : "在垃圾桶裡"}。`,
  ],
  [
    /^"(.+)" is hidden\. Nobody sees it until the hidden flag is turned off\.$/,
    (m) => `「${m[1]}」已隱藏。在關閉隱藏之前沒有人看得到。`,
  ],
  [/^"(.+)" is not hidden or in the trash\.$/, (m) => `「${m[1]}」沒有隱藏，也不在垃圾桶裡。`],
  [
    /^The project is for (.+)\. Their tickets grant (.+)\.$/,
    (m) =>
      `此專案屬於 ${zhList(m[1] ?? "")}。${
        m[2] === "no products at all"
          ? "他們的票券沒有授予任何產品。"
          : `他們的票券授予 ${zhList(m[2] ?? "")}。`
      }`,
  ],
  [/^Reached through (.+)\.$/, (m) => `透過 ${zhList(m[1] ?? "")} 取得。`],
  [
    /^No classroom course teaches (.+), so nothing sets a level for it\.$/,
    (m) => `沒有任何班級課程教 ${zhList(m[1] ?? "")}，因此沒有東西為它設定等級。`,
  ],
  [/^Their classroom teaches (.+)\.$/, (m) => `他們的班級有教 ${zhPlacements(m[1] ?? "")}。`],
  [
    /^Their class reaches (.+)\. The project is level (.+?)\.( That is the product's base level.+)?$/,
    (m) => `他們的班級最高到 ${m[1]}，此專案為等級 ${m[2]}。${m[3] ? ZERO_LEVEL_NOTE : ""}`,
  ],
  [
    /^Project is level (.+?); their class reaches (.+)\.$/,
    (m) => `此專案為等級 ${m[1]}，其班級最高到 ${m[2]}。`,
  ],
  [
    /^The project itself passes, but the folder above it — "(.+)" \(level (\d+)\) — does not, so the student never reaches it from the library\.$/,
    (m) =>
      `專案本身通過了，但上層資料夾「${m[1]}」（等級 ${m[2]}）沒有通過，所以學生瀏覽時永遠抵達不了。`,
  ],
  [
    /^Every folder above this project is visible to them too\.$/,
    () => "此專案上層的每個資料夾，他們也都看得到。",
  ],
];

export function translateLabel(label: string, locale: Locale): string {
  return locale === "en" ? label : (CHECK_LABELS[label] ?? label);
}

export function translateDetail(detail: string, locale: Locale): string {
  if (locale === "en") return detail;
  for (const [pattern, toChinese] of DETAILS) {
    const match = detail.match(pattern);
    if (match) return toChinese(match);
  }
  return detail;
}

export function translateRole(role: string, locale: Locale): string {
  return locale === "en" ? role : (ROLES[role] ?? role);
}

export function translateCapLabel(capLabel: string, locale: Locale): string {
  if (locale === "en") return capLabel;
  if (capLabel === "no class") return "沒有班級";
  return capLabel === "every level" ? "所有等級" : capLabel;
}
